import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { catchError, map, Observable, throwError } from 'rxjs';
import { ZraApiError } from '../zra-product-detail/zra-product-detail.service';
import {
  getSyncStatusDisplayName,
  SyncStatus,
} from '@boma/supplier-portal/shared/entities';
export class CustomerTpinResponse {
  id: string;
  entityCustomerId: string;
  entityId: string;
  countryId: string;
  countryName: string;
  tpin: string;
  externalCustomerId: string;
  syncStatus: SyncStatus;
  lastSyncedAt: string;
  lastSyncError: string;

  constructor(init?: Partial<CustomerTpinResponse>) {
    Object.assign(this, init);
  }

  getSyncStatusDisplayName(): string {
    return getSyncStatusDisplayName(this.syncStatus);
  }
}

export interface UpdateCustomerTpinRequest {
  customerId: string;
  tpin: string;
}

export interface CustomerTpinEntry {
  customerId: string;
  tpin: string;
}

export interface BatchUpdateCustomerTpinRequest {
  countryId: string;
  customerTpins: CustomerTpinEntry[];
}

export interface CustomerTpinUpdateResult {
  customerId: string;
  tpin: string;
  success: boolean;
  errorMessage: string;
}

export interface BatchUpdateCustomerTpinResponse {
  successCount: number;
  failureCount: number;
  results: CustomerTpinUpdateResult[];
}

@Injectable({
  providedIn: 'root',
})
export class ZraCustomerTpinService {
  private base: string;
  private version: string;
  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private http: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  getCustomerTpin(
    entityId: string,
    customerId: string
  ): Observable<CustomerTpinResponse> {
    return this.http
      .get<CustomerTpinResponse>(
        `${this.base}api/v${this.version}/${entityId}/customers/zra/${customerId}`
      )
      .pipe(
        map((response) => new CustomerTpinResponse(response)),
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, 'Failed to get customer TPIN details')
        )
      );
  }

  updateCustomerTpin(
    entityId: string,
    request: UpdateCustomerTpinRequest
  ): Observable<CustomerTpinResponse> {
    return this.http
      .put<CustomerTpinResponse>(
        `${this.base}api/v${this.version}/${entityId}/customers/zra`,
        request
      )
      .pipe(
        map((response) => new CustomerTpinResponse(response)),
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, 'Failed to update customer TPIN details')
        )
      );
  }

  batchUpdateCustomerTpin(
    entityId: string,
    request: BatchUpdateCustomerTpinRequest
  ): Observable<BatchUpdateCustomerTpinResponse> {
    return this.http
      .post<BatchUpdateCustomerTpinResponse>(
        `${this.base}api/v${this.version}/${entityId}/customers/zra/batch`,
        request
      )
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleError(
            error,
            'Failed to batch update customer TPIN details'
          )
        )
      );
  }

  syncCustomerTpin(
    entityId: string,
    customerId: string
  ): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(
        `${this.base}api/v${this.version}/${entityId}/customers/zra/${customerId}/sync`,
        {}
      )
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, 'Failed to sync customer TPIN with ZRA')
        )
      );
  }

  queueCustomerTpin(
    entityId: string,
    customerId: string
  ): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(
        `${this.base}api/v${this.version}/${entityId}/customers/zra/${customerId}/queue`,
        {}
      )
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, 'Failed to sync customer TPIN with ZRA')
        )
      );
  }

  private handleError(
    error: HttpErrorResponse,
    defaultMessage: string
  ): Observable<never> {
    let errorMessage = defaultMessage;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Server-side error
      try {
        const apiError = error.error as ZraApiError;
        errorMessage =
          apiError.message ||
          `Server error: ${error.status} - ${error.statusText}`;
        if (apiError.details) {
          errorMessage += ` (${apiError.details})`;
        }
      } catch {
        errorMessage = `Server error: ${error.status} - ${
          error.statusText || defaultMessage
        }`;
      }
    }

    return throwError(() => ({
      message: errorMessage,
      status: error.status,
      originalError: error,
    }));
  }
}
